import { useCallback, useMemo, useState } from "react";
import { useTerritories } from "../contexts";
import {
    CaseFile,
    CaseFileCategories,
    FormSlugs,
    Procedure,
    ProcedureTypes,
    QueryType,
    RecoveryAppFormData,
    TissueImageTypes,
    TissueTypes,
} from "../models";
import {
    useCaseFileCategoryQuery,
    useCaseFileQuery,
    useCaseQuery,
    useFormDataQuery,
    usePersonQuery,
    useShipmentQuery,
    useTissueQuery,
    useUserBasicQuery,
} from "../queries";
import { useTissueStorage } from "./useTissueStorage";
import { CaseFileUrlRecord, initialGradingSummaryConfig, ISummaryOfRecordsFormData } from "../components";
import {
    calculateDeathToPreservation,
    displayDate,
    displayDateAndTime,
    getProcurementTimeUtc,
} from "../helper-functions";
import { useAutoSummaryFlat, useTissueProcedure } from ".";
import { Endpoints } from "../api";
import { useAxios } from "./useAxios";
import { getGradingSummaryConfig } from "../components/tissues";

export interface TIFFormDataProps {
    tissueId: number | undefined;
}

export const useTIFFormData = ({ tissueId }: Readonly<TIFFormDataProps>) => {
    const { getBlob } = useAxios();
    const {
        detailQuery: { data: tissue },
    } = useTissueQuery({ queryType: QueryType.Detail, id: tissueId });

    const {
        detailQuery: { data: _case },
    } = useCaseQuery({ id: tissue?.caseId, includeDetails: true });
    const { id: caseId, donor, referral, determination } = _case ?? {};

    const {
        productCodeQuery: { data: productCodeData },
    } = useTissueQuery({ queryType: "productCode", id: tissueId });
    const productCode = productCodeData?.productCode;
    const { territories } = useTerritories();
    const {
        latestStorageSolution,
        latestLotNumber,
        latestExpDate,
        originalStorageSolution,
        originalExpDate,
        originalLotNumber,
    } = useTissueStorage(tissue?.id);

    const {
        listQuery: { data: recoveryForms, isLoading },
    } = useFormDataQuery({ caseId, formSlug: FormSlugs.Recovery });
    const recoveryForm = useMemo(() => {
        return recoveryForms?.[0];
    }, [recoveryForms]);
    const procuredByUserName = useMemo(() => {
        const signature = recoveryForm?.signatures?.[recoveryForm?.signatures?.length - 1];
        return signature?.userName;
    }, [recoveryForm]);
    const recoveryData = useMemo<RecoveryAppFormData>(() => {
        return recoveryForm?.data ?? ({} as RecoveryAppFormData);
    }, [recoveryForm]);

    const {
        listQuery: { data: summaryOfRecords },
    } = useFormDataQuery({ caseId, formSlug: FormSlugs.SummaryOfRecords });
    const summaryOfRecordsData = useMemo<ISummaryOfRecordsFormData>(() => {
        return summaryOfRecords?.[0]?.data ?? ({} as ISummaryOfRecordsFormData);
    }, [summaryOfRecords]);

    const procedures = tissue?.tissueProcedures?.map((tp) => tp.procedure).filter(Boolean) as Procedure[];

    const latestTissueProcedure = tissue?.tissueProcedures
        ?.filter((tp) => !!tp.procedure?.performedOn)
        ?.sort((a, b) => {
            const dateA = new Date(a.procedure?.performedOn!);
            const dateB = new Date(b.procedure?.performedOn!);
            return dateB.getTime() - dateA.getTime();
        })?.[0];

    const latestProcedure = latestTissueProcedure?.procedure;

    const laboratoryExcisionProcedure = procedures?.filter(
        (p) => p.procedureType?.name === ProcedureTypes.LabCornealExcision
    )?.[0];

    const laboratoryExcisionPerformedOn = useMemo(() => {
        return displayDateAndTime(laboratoryExcisionProcedure?.performedOn);
    }, [laboratoryExcisionProcedure?.performedOn]);

    const surgery = tissue?.backupSurgery ?? tissue?.surgery;
    const tissueOrder = tissue?.orders?.[0];

    const userIds = useMemo(() => {
        const result: number[] = [];
        if (tissueOrder?.shipment?.packedByUserId) {
            result.push(tissueOrder?.shipment?.packedByUserId);
        }
        if (determination?.donorEligibilitySignedByUserId) {
            result.push(determination?.donorEligibilitySignedByUserId);
        }
        if (laboratoryExcisionProcedure?.performedByUserId) {
            result.push(laboratoryExcisionProcedure?.performedByUserId);
        }
        if (latestProcedure?.performedByUserId) {
            result.push(latestProcedure?.performedByUserId);
        }

        return result;
    }, [
        determination?.donorEligibilitySignedByUserId,
        laboratoryExcisionProcedure?.performedByUserId,
        latestProcedure?.performedByUserId,
        tissueOrder?.shipment?.packedByUserId,
    ]);

    const {
        listQuery: { data: users },
    } = useUserBasicQuery({ ids: userIds });

    const packedBy = useMemo(() => {
        return users?.find((u) => u.id === tissueOrder?.shipment?.packedByUserId);
    }, [users, tissueOrder?.shipment?.packedByUserId]);

    const donorEligibilitySignedBy = useMemo(() => {
        return users?.find((u) => u.id === determination?.donorEligibilitySignedByUserId);
    }, [users, determination?.donorEligibilitySignedByUserId]);

    const latestProcedurePerformedBy = useMemo(() => {
        return users?.find((u) => u.id === latestProcedure?.performedByUserId);
    }, [latestProcedure?.performedByUserId, users]);

    const laboratoryExcisionPerformedBy = useMemo(() => {
        return users?.find((u) => u.id === laboratoryExcisionProcedure?.performedByUserId);
    }, [laboratoryExcisionProcedure?.performedByUserId, users]);

    const {
        detailQuery: { data: tissueOrderSurgeon },
    } = usePersonQuery({ queryType: QueryType.Detail, id: tissueOrder?.surgeonUserId });

    const surgeon = tissueOrderSurgeon ?? surgery?.surgeon;
    const surgeonName = surgeon ? `${surgeon.firstName} ${surgeon.lastName}` : "";
    const locationAccount = tissueOrder?.usageLocation ?? surgery?.locationAccount;

    const {
        latestForTissueQuery: { data: latestShipment },
    } = useShipmentQuery({ tissueId: tissue?.id?.toString(), queryType: QueryType.Detail });

    const tissueExpirationDate = useMemo(() => {
        const preservationDateUtc = getProcurementTimeUtc(recoveryData);
        if (!preservationDateUtc) return "";

        const preservationDate = new Date(preservationDateUtc);
        preservationDate.setDate(preservationDate.getDate() + 14);
        return preservationDate.toString();
    }, [recoveryData]);
    const deathToPreservation = calculateDeathToPreservation(_case?.referral?.timeOfDeathUtc, recoveryData);
    const dToPInvalid = deathToPreservation?.hours === -1 && deathToPreservation?.minutes === -1;

    const dToPDisplayValue =
        isLoading || !deathToPreservation || dToPInvalid
            ? ""
            : `${deathToPreservation.hours}hr ${deathToPreservation.minutes}min`;

    const pastMedicalHistory = useMemo(
        () => summaryOfRecordsData.pastMedicalHistory ?? {},
        [summaryOfRecordsData.pastMedicalHistory]
    );
    const pastMedicalHistoryKeys = useMemo(
        () =>
            Object.keys(pastMedicalHistory).filter((key) => {
                return !!pastMedicalHistory[key].value;
            }),
        [pastMedicalHistory]
    );
    const pastProceduresSurgicalHistory = useMemo(
        () => summaryOfRecordsData.pastProceduresSurgicalHistory ?? {},
        [summaryOfRecordsData]
    );
    const pastProceduresSurgicalHistoryKeys = useMemo(
        () =>
            Object.keys(pastProceduresSurgicalHistory).filter((key) => {
                return !!pastProceduresSurgicalHistory[key].value;
            }),
        [pastProceduresSurgicalHistory]
    );
    const wbcExists = useMemo(() => {
        return (
            summaryOfRecordsData?.laboratoryResults?.WhiteBloodCellCount1 ||
            summaryOfRecordsData?.laboratoryResults?.WhiteBloodCellCount2 ||
            summaryOfRecordsData?.laboratoryResults?.WhiteBloodCellCount3
        );
    }, [summaryOfRecordsData]);

    const wbcValue = useMemo(() => {
        if (summaryOfRecordsData.laboratoryResults === undefined) return "";
        const {
            WhiteBloodCellCount1,
            WhiteBloodCellCount1Date,
            WhiteBloodCellCount2,
            WhiteBloodCellCount2Date,
            WhiteBloodCellCount3,
            WhiteBloodCellCount3Date,
        } = summaryOfRecordsData.laboratoryResults;
        let wbcVal = "";
        if (WhiteBloodCellCount1 !== undefined) {
            wbcVal = `${WhiteBloodCellCount1} on ${displayDate(WhiteBloodCellCount1Date)}`;
        }
        if (WhiteBloodCellCount2 !== undefined) {
            wbcVal = wbcVal.concat(", ", `${WhiteBloodCellCount2} on ${displayDate(WhiteBloodCellCount2Date)}`);
        }
        if (WhiteBloodCellCount3 !== undefined) {
            wbcVal = wbcVal.concat(", ", `${WhiteBloodCellCount3} on ${displayDate(WhiteBloodCellCount3Date)}`);
        }
        return wbcVal;
    }, [summaryOfRecordsData]);

    const tempExists = useMemo(() => {
        return (
            summaryOfRecordsData?.laboratoryResults?.Temperatures1 ||
            summaryOfRecordsData?.laboratoryResults?.Temperatures2 ||
            summaryOfRecordsData?.laboratoryResults?.Temperatures3
        );
    }, [summaryOfRecordsData]);

    const tempValue = useMemo(() => {
        if (summaryOfRecordsData?.laboratoryResults === undefined) return "";
        const { Temperatures1, Temperatures1Date, Temperatures2, Temperatures2Date, Temperatures3, Temperatures3Date } =
            summaryOfRecordsData.laboratoryResults;
        let tempValue = "";

        if (Temperatures1 !== undefined) {
            tempValue = `${Temperatures1} on ${displayDate(Temperatures1Date)}`;
        }
        if (Temperatures2 !== undefined) {
            tempValue = tempValue.concat(", ", `${Temperatures2} on ${displayDate(Temperatures2Date)}`);
        }
        if (Temperatures3 !== undefined) {
            tempValue = tempValue.concat(", ", `${Temperatures3} on ${displayDate(Temperatures3Date)}`);
        }
        return tempValue;
    }, [summaryOfRecordsData]);

    const bloodCultureExists = useMemo(() => {
        return summaryOfRecordsData?.laboratoryResults?.BloodResults;
    }, [summaryOfRecordsData?.laboratoryResults?.BloodResults]);

    const urineCultureExists = useMemo(() => {
        return summaryOfRecordsData?.laboratoryResults?.UrineResults;
    }, [summaryOfRecordsData?.laboratoryResults?.UrineResults]);

    const sputumCultureExists = useMemo(() => {
        return summaryOfRecordsData?.laboratoryResults?.SputumResults?.length > 0;
    }, [summaryOfRecordsData?.laboratoryResults?.SputumResults]);

    const otherCultureExists = useMemo(() => {
        return summaryOfRecordsData?.laboratoryResults?.OtherResults;
    }, [summaryOfRecordsData?.laboratoryResults?.OtherResults]);

    const bloodCultureValue = useMemo(() => {
        if (!summaryOfRecordsData?.laboratoryResults) return "";
        const { BloodResults, BloodDate } = summaryOfRecordsData.laboratoryResults;
        let bloodCultureValue = "";
        if (BloodResults !== undefined) {
            bloodCultureValue = `${BloodResults} on ${displayDate(BloodDate)}`;
        }
        return bloodCultureValue;
    }, [summaryOfRecordsData.laboratoryResults]);

    const urineCultureValue = useMemo(() => {
        if (!summaryOfRecordsData?.laboratoryResults) return "";
        const { UrineResults, UrineDate } = summaryOfRecordsData.laboratoryResults;
        let urineCultureValue = "";
        if (UrineResults !== undefined) {
            urineCultureValue = `${UrineResults} on ${displayDate(UrineDate)}`;
        }
        return urineCultureValue;
    }, [summaryOfRecordsData.laboratoryResults]);

    const sputumCultureValue = useMemo(() => {
        if (!summaryOfRecordsData?.laboratoryResults) return "";
        const { SputumResults, SputumDate } = summaryOfRecordsData.laboratoryResults;
        let sputumCultureValue = "";
        if (SputumResults !== undefined) {
            sputumCultureValue = `${SputumResults} on ${displayDate(SputumDate)}`;
        }
        return sputumCultureValue;
    }, [summaryOfRecordsData.laboratoryResults]);

    const otherCultureValue = useMemo(() => {
        if (!summaryOfRecordsData?.laboratoryResults) return "";
        const { OtherResults, OtherDate } = summaryOfRecordsData.laboratoryResults;
        let otherCultureValue = "";
        if (OtherResults !== undefined) {
            otherCultureValue = `${OtherResults} on ${displayDate(OtherDate)}`;
        }
        return otherCultureValue;
    }, [summaryOfRecordsData.laboratoryResults]);

    const getSerologyValues = (value: string, noBlank?: boolean) => {
        if (value === undefined) return noBlank ? "" : "___";
        switch (value.toLowerCase()) {
            case "pending":
                return "Pending";
            case "positive":
                return "Positive";
            case "negative":
                return "Negative";
            case "not-run":
                return "Not Run";
        }
    };

    const dedSignedByTerritory = territories?.find((t) => t.id === donorEligibilitySignedBy?.territoryId);

    const donorEligibilitySignature = useMemo(() => {
        if (!determination?.donorEligibilitySignedOn || !donorEligibilitySignedBy) return "";
        let result = `${donorEligibilitySignedBy?.firstName} ${donorEligibilitySignedBy?.lastName}`;
        if (dedSignedByTerritory?.label) {
            result += ` (${dedSignedByTerritory?.label})`;
        }
        result += `, ${displayDateAndTime(determination?.donorEligibilitySignedOn)}`;
        return result;
    }, [determination?.donorEligibilitySignedOn, donorEligibilitySignedBy, dedSignedByTerritory?.label]);

    const {
        listQuery: { data: caseFileCategories, isFetching: isCaseFileCategoriesFetching },
    } = useCaseFileCategoryQuery();

    const tissueGradingCategory = useMemo(
        () => (caseFileCategories ?? []).find((category) => category.name === CaseFileCategories.TissueGrading),
        [caseFileCategories]
    );

    const {
        listQuery: { data: initialGradingCaseFiles, isFetching: isCaseFilesFetching },
    } = useCaseFileQuery({ caseId, caseFileCategoryId: tissueGradingCategory?.id });

    const initialGradingSlitLampFiles = initialGradingCaseFiles?.filter(
        (caseFile) =>
            caseFile.type === TissueImageTypes.SlitLamp &&
            caseFile.createdOn !== undefined &&
            caseFile.tissueId === tissueId
    );

    const initialGradingSpecularFiles = initialGradingCaseFiles?.filter(
        (caseFile) =>
            caseFile.type === TissueImageTypes.Specular &&
            caseFile.createdOn !== undefined &&
            caseFile.tissueId === tissueId
    );

    const latestInitialGradingSlitLamp = initialGradingSlitLampFiles?.sort((a, b) => {
        const dateA = new Date(a.createdOn!);
        const dateB = new Date(b.createdOn!);
        return dateB.getTime() - dateA.getTime();
    })?.[0];

    const latestInitialGradingSpecular = initialGradingSpecularFiles?.sort((a, b) => {
        const dateA = new Date(a.createdOn!);
        const dateB = new Date(b.createdOn!);
        return dateB.getTime() - dateA.getTime();
    })?.[0];

    const initialGradingFiles = [latestInitialGradingSlitLamp, latestInitialGradingSpecular].filter(
        Boolean
    ) as CaseFile[];

    const postProcessingCategory = useMemo(
        () => (caseFileCategories ?? []).find((category) => category.name === CaseFileCategories.TissuePostProcessing),
        [caseFileCategories]
    );
    const {
        listQuery: { data: postProcessingCaseFiles },
    } = useCaseFileQuery({ caseId, caseFileCategoryId: postProcessingCategory?.id });

    const postProcessingSlitLampFiles = postProcessingCaseFiles?.filter(
        (file) =>
            file.type === TissueImageTypes.SlitLamp &&
            file.createdOn !== undefined &&
            file.tissueId === tissueId &&
            file.procedureId === latestProcedure?.id
    );
    const postProcessingSpecularFiles = postProcessingCaseFiles?.filter(
        (file) =>
            file.type === TissueImageTypes.Specular &&
            file.createdOn !== undefined &&
            file.tissueId === tissueId &&
            file.procedureId === latestProcedure?.id
    );
    const latestPostProcessingSlitLamp = postProcessingSlitLampFiles?.sort((a, b) => {
        const dateA = new Date(a.createdOn!);
        const dateB = new Date(b.createdOn!);
        return dateB.getTime() - dateA.getTime();
    })?.[0];

    const latestPostProcessingSpecular = postProcessingSpecularFiles?.sort((a, b) => {
        const dateA = new Date(a.createdOn!);
        const dateB = new Date(b.createdOn!);
        return dateB.getTime() - dateA.getTime();
    })?.[0];

    const postProcessingFiles = [latestPostProcessingSlitLamp, latestPostProcessingSpecular].filter(
        Boolean
    ) as CaseFile[];

    const [caseFileUrls, setCaseFileUrls] = useState<CaseFileUrlRecord>({});
    const [caseFileUrlsLoaded, setCaseFileUrlsLoaded] = useState(false);

    const { postProcedureEval } = useTissueProcedure({
        procedureId: latestProcedure?.id,
        tissueId: tissueId,
    });

    const postProcessingOverallRating = useMemo(() => {
        let rating = "";
        switch (postProcedureEval?.data?.rating?.suitableForTransplant) {
            case "yes":
                return "Suitable for Transplant";
            case "no":
                return "Not Suitable for Transplant";
        }
        return rating;
    }, [postProcedureEval?.data?.rating?.suitableForTransplant]);

    const initialEval = tissue?.initialEval;
    const initialEvalSummary = useAutoSummaryFlat(initialGradingSummaryConfig, initialEval?.data);
    const postProcedureSummaryConfig = getGradingSummaryConfig(
        tissue?.tissueType?.name as TissueTypes,
        latestProcedure?.procedureType?.name as ProcedureTypes
    );
    const postProcedureEvalSummary = useAutoSummaryFlat(postProcedureSummaryConfig, postProcedureEval?.data);

    const fetchGradingCaseFileUrls = useCallback(
        async (caseFiles: CaseFile[]) => {
            const urls: CaseFileUrlRecord = {};
            if (
                isCaseFileCategoriesFetching ||
                isCaseFilesFetching ||
                !caseId ||
                !caseFiles?.length ||
                caseFileUrlsLoaded
            )
                return;
            for (const caseFile of caseFiles) {
                if (!caseFile?.path) continue;
                try {
                    const blob = await getBlob({ url: `${Endpoints.CaseFiles}/download/${caseFile.id}` });
                    const url = URL.createObjectURL(blob);
                    urls[caseFile?.id] = url;
                } catch (error) {
                    console.error(`Error fetching blob for case file ${caseFile.id}:`, error);
                    urls[caseFile?.id] = "";
                }
            }
            setCaseFileUrls(urls);
            setCaseFileUrlsLoaded(true);
        },
        [isCaseFileCategoriesFetching, isCaseFilesFetching, caseId, getBlob, caseFileUrlsLoaded]
    );

    const getSuitability = () => {
        return tissue?.initialEval?.data?.suitability?.transplantOptions
            .filter((option: any) => option !== false)
            .join(" ");
    };

    const tissueNumWithProcedure = useMemo(() => {
        let tissueNum = `Tissue # ${tissue?.number}`;
        switch (tissue?.tissueType?.name) {
            case TissueTypes.Dmek: {
                switch (latestProcedure?.procedureType?.name) {
                    case ProcedureTypes.DMEKPreloaded:
                        return tissueNum + " PL DMEK";
                    case ProcedureTypes.DMEKPatientReady:
                        return tissueNum + " PR DMEK";
                    default:
                        return tissueNum + " DMEK";
                }
            }
            case TissueTypes.Precut:
            case TissueTypes.Precut1:
            case TissueTypes.Precut2: {
                return tissueNum + " DSAEK";
            }
            case TissueTypes.Lenticule:
                return tissueNum + " LENTICULE";
            case TissueTypes.Sclera:
            case TissueTypes.Sclera1:
            case TissueTypes.Sclera2:
            case TissueTypes.Quarter1:
            case TissueTypes.Quarter2:
            case TissueTypes.Quarter3:
            case TissueTypes.Quarter4:
                return tissueNum + " SCLERA";
            case TissueTypes.Remnant:
                return tissueNum + " CORNEAL REMNANT";

            case TissueTypes.Cairs:
            case TissueTypes.Cairs1:
            case TissueTypes.CairsCtr:
            case TissueTypes.CairsSeg1:
            case TissueTypes.CairsSeg2:
                return tissueNum + " CAIRS";
        }

        return tissueNum;
    }, [tissue?.number, tissue?.tissueType?.name, latestProcedure?.procedureType?.name]);

    const noPostProcEvalNeededForTissueTypes: string[] = useMemo(() => {
        return [TissueTypes.Precut1, TissueTypes.Precut2];
    }, []);

    const hasInitialGrading = useMemo(() => {
        return !!initialEval?.signedByName || !!initialGradingFiles.length;
    }, [initialEval?.signedByName, initialGradingFiles]);

    const hasPostProcedureGrading = useMemo(() => {
        return (
            !noPostProcEvalNeededForTissueTypes.includes(tissue?.tissueType?.name ?? "") &&
            (!!postProcedureEval?.signedByName || !!postProcessingFiles.length)
        );
    }, [
        noPostProcEvalNeededForTissueTypes,
        postProcedureEval?.signedByName,
        postProcessingFiles.length,
        tissue?.tissueType?.name,
    ]);

    return {
        tissue,
        donor,
        referral,
        caseId,
        _case,
        productCode,
        territories,
        latestStorageSolution,
        latestLotNumber,
        latestExpDate,
        recoveryData,
        summaryOfRecordsData,
        tissueOrder,
        surgeonName,
        locationAccount,
        packedBy,
        latestShipment,
        tissueExpirationDate,
        dToPDisplayValue,
        pastMedicalHistory,
        pastMedicalHistoryKeys,
        pastProceduresSurgicalHistory,
        pastProceduresSurgicalHistoryKeys,
        wbcExists,
        wbcValue,
        tempExists,
        tempValue,
        getSerologyValues,
        donorEligibilitySignature,
        fetchGradingCaseFileUrls,
        initialGradingFiles,
        caseFileUrls,
        initialEval,
        initialEvalSummary,
        getSuitability,
        latestProcedure,
        latestTissueProcedure,
        tissueNumWithProcedure,
        latestInitialGradingSlitLamp,
        latestInitialGradingSpecular,
        postProcedureEval,
        postProcessingOverallRating,
        postProcessingSlitLampFiles,
        latestPostProcessingSlitLamp,
        latestPostProcessingSpecular,
        bloodCultureExists,
        urineCultureExists,
        sputumCultureExists,
        otherCultureExists,
        bloodCultureValue,
        urineCultureValue,
        sputumCultureValue,
        otherCultureValue,
        latestProcedurePerformedBy,
        originalStorageSolution,
        originalLotNumber,
        originalExpDate,
        postProcessingFiles,
        laboratoryExcisionPerformedOn,
        laboratoryExcisionPerformedBy,
        postProcedureEvalSummary,
        procuredByUserName,
        hasInitialGrading,
        hasPostProcedureGrading,
    };
};
